import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../../hooks/useDatabase';
import { usePublicLanding } from '../../hooks/usePublicContent';
import { submitLead } from '../../services/leadService';
import { readOrigin } from '../../utils/utm';
import { isUuid, logFailure } from '../../utils/diagnostics';
import { isValidUrl, postSubmitConfig } from '../../utils/postSubmit';
import { LandingRenderer } from './LandingRenderer';
import { DynamicFormResult } from './DynamicForm';
import { Button } from '../ui/Button';
import { Form, LandingPage } from '../../types';
interface LandingViewProps {
  slug: string;
  /** Muestra los accesos a la plataforma en la barra superior (home de AVA). */
  showAppNav?: boolean;
  /** Contenido estático usado si la landing no existe en la base de datos. */
  fallback?: {
    landing: LandingPage;
    form?: Form;
  };
}
export function LandingView({
  slug,
  showAppNav = false,
  fallback
}: LandingViewProps) {
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const {
    status,
    message,
    retry
  } = usePublicLanding(slug);
  const landing = db.landings.find(l => l.slug === slug) ?? fallback?.landing;
  const organization = landing ? db.organizations.find(o => o.id === landing.organizationId) : undefined;
  const form = (landing?.formId ? db.forms.find(f => f.id === landing.formId) : undefined) ?? fallback?.form;

  // Mientras la consulta esté en curso NUNCA se muestra «no existe»: solo un
  // cargador limpio. El 404 se reserva para `no_encontrada`.
  if (status === 'cargando' && !landing) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-647" data-mp="k6lir6uaq22p">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-648" data-mp="p5y41n5ir136" />
        <span className="sr-only" data-id="element-649" data-mp="2qv41kkbf4f6l">Cargando la página…</span>
      </main>;
  }
  if (status === 'error' && !landing) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-650" data-mp="2m2swz45qg57p">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" aria-hidden="true" data-id="element-651" data-mp="2i2ih8zs520fn" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-652" data-mp="2d4qsgemsiufo">
          No hemos podido cargar la página
        </h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-653" data-mp="39rc7lqc98u2y">
          Comprueba tu conexión e inténtalo de nuevo.
          {message ? ` (${message})` : ''}
        </p>
        <Button onClick={retry} data-id="element-654" data-mp="30rgpt2o720ty">Reintentar</Button>
      </main>;
  }
  if (!landing) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-655" data-mp="2vsm0rizmvg1e">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-656" data-mp="1pewf7vyk73uw" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-657" data-mp="1kdgslzgqgpae">Esta página no existe</h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-658" data-mp="1ab3cihu7kfoi">
          La landing que buscas no está publicada o el enlace ha cambiado.
        </p>
        <Link to="/" data-id="element-659" data-mp="1f9xzlvz7kqef">
          <Button variant="secondary" data-id="element-660" data-mp="1k8rarhr0w5bf">Volver al inicio</Button>
        </Link>
      </main>;
  }

  // El logotipo de la landing hereda el de la empresa si no se ha definido uno.
  const resolved = {
    ...landing,
    pageConfig: {
      ...landing.pageConfig,
      logoUrl: landing.pageConfig.logoUrl || organization?.brand.logoUrl || organization?.logoUrl
    }
  };
  async function handleSubmit(result: DynamicFormResult) {
    if (!landing || !form) return;
    setError(null);

    // La plantilla estática de respaldo tiene ids que no son UUID: si se está
    // mostrando esa, no existe en la base de datos y no puede crear leads.
    if (!isUuid(form.id) || !isUuid(landing.id)) {
      logFailure('submitLead', {
        message: 'Se está mostrando la landing de ejemplo incluida en el código, no una landing real de la base de datos.',
        details: `landing_id="${landing.id}", form_id="${form.id}", slug="${slug}"`,
        hint: 'Publica tu landing desde el panel y visita su URL pública (/landing/tu-slug).'
      }, {
        landingId: landing.id,
        formId: form.id,
        slug
      });
      const message = 'Esta es la landing de ejemplo del sistema y no está conectada a ninguna empresa, por lo que no puede guardar contactos. Publica tu landing desde el panel y usa su enlace público.';
      setError(message);
      throw new Error(message);
    }
    try {
      await submitLead({
        organizationId: landing.organizationId,
        formId: form.id,
        landingId: landing.id,
        contact: result.contact,
        answers: result.answers,
        origin: readOrigin(location.search, {
          landingId: landing.id,
          formId: form.id
        })
      });
      // El lead ya está guardado. Solo ahora se ejecuta la acción posterior
      // configurada en el formulario.
      const action = postSubmitConfig(form);
      if (action.action === 'url' && isValidUrl(action.redirectUrl)) {
        window.location.href = action.redirectUrl!.trim();
        return;
      }
      if (action.action === 'whatsapp') {
        // El botón y el mensaje se muestran dentro del propio formulario.
        return;
      }
      if (action.action === 'mensaje') return;

      // Flujo previsto: formulario multipaso → cualificación → calendario.
      // Si la landing tiene bloque de calendario, el visitante continúa ahí
      // sin salir de la página; si no, va a la página de gracias.
      const hasCalendar = landing.pageConfig.blocks.some(block => block.type === 'calendario' && block.visible);
      if (hasCalendar) {
        document.getElementById('calendario')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        return;
      }
      navigate(`/gracias/${landing.slug}`);
    } catch (err) {
      // El mensaje real se propaga al formulario, que lo muestra en línea.
      setError(err instanceof Error ? err.message : 'No hemos podido enviar tu solicitud.');
      throw err;
    }
  }
  return <main className="w-full bg-background" data-id="element-661" data-mp="1njll7td0034r">
      {error && <p role="alert" className="bg-red-50 px-5 py-3 text-center text-sm text-red-700" data-id="element-662" data-mp="1ip7tc0xjoe4u">
          {error}
        </p>}
      <LandingRenderer landing={resolved} form={form} onSubmit={handleSubmit} authLinks={showAppNav} data-id="element-663" data-mp="1dr61orm1z6hr" />
    </main>;
}